import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import connectToDataRoomIcon from "../assets/detail.svg";
import uploadDocsEmptyState from "../assets/upload_docs_empty_state.svg";
import uploadIcon from "../assets/upload_file_icon.svg";
import DataRoomConnectionPopup from "../components/DataRoomConnectionPopup";
import { useConnectDataSource } from "../hooks/useConnectDataSource";
import { useFileLibrary } from "../hooks/useFileLibrary";
import { useUploadFlow } from "../hooks/useUploadFlow";
import { useToast } from "../context/ToastContext";

const GetStarted: React.FC = () => {
	const [isDataRoomPopupOpen, setIsDataRoomPopupOpen] = useState(false);
	const navigate = useNavigate();
	const { showToast } = useToast();
	const handleDataRoomConnect = useConnectDataSource();
	// The upload keeps running in the app-wide manager, so the user is sent
	// on the moment the files are handed off: to the Files home (the first
	// deal now exists).
	const uploadFlow = useUploadFlow(() => navigate("/files"));

	return (
		<div className="flex-1 flex flex-row bg-white align-start relative overflow-hidden">
			{/* Background gradient effect */}
			<div className="flex-1 flex flex-col px-4 sm:px-8 lg:px-16 relative z-10">
				<div className="absolute w-full h-full bg-gradient-to-r from-[rgba(0,76,75,0.15)] to-[rgba(42,157,144,0.15)] blur-[250px] transform -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2"></div>

				<div
					className="flex-1 flex flex-col justify-start px-4 sm:px-8 lg:px-16 relative z-10"
					style={{ marginTop: "124px" }}
				>
					<div className="max-w-4xl">
						{/* Main heading */}
						<div
							className="font-normal text-[#111827] leading-tight tracking-[-0.03em] mb-4"
							style={{
								fontSize: "clamp(2.25rem, 4vw, 3.75rem)",
								lineHeight: "1.1",
							}}
						>
							{`${"Let's get started."}`}
						</div>

						{/* Description */}
						<div
							className="text-[#4B5563] leading-relaxed tracking-[-0.01em] mb-4 max-w-lg"
							style={{
								fontSize: "clamp(1rem, 2.5vw, 1.125rem)",
								lineHeight: "1.625",
							}}
						>
							To get started, upload your deal documents or connect directly to
							your firm's drive. Doppler will get started on your key workflows,
							such as reviewing a CIM, building a model, or reviewing a credit
							agreement.
						</div>

						{/* Action buttons */}
						<div className="flex flex-col sm:flex-row gap-4">
							<button
								onClick={uploadFlow.start}
								className="flex items-center justify-center gap-2 px-4 py-2 bg-[#004C4B] text-white rounded-md hover:bg-[#003a3a] transition-colors"
							>
								<div
									className="font-medium"
									style={{ fontSize: "14px", lineHeight: "100%" }}
								>
									Upload File
								</div>
								<img
									src={uploadIcon}
									alt="Upload"
									style={{ width: "14px", height: "14px" }}
								/>
							</button>

							{/* Data room connections are turned off for now. The button stays
							    clickable on purpose so the click can explain why. */}
							<button
								onClick={() =>
									showToast("Data room connections are not enabled yet.", "info")
								}
								style={{ backgroundColor: "#ECECEC" }}
								className="flex items-center justify-center gap-2 rounded-md px-4 py-2 text-[#9CA3AF] opacity-60"
							>
								<div
									className="font-medium"
									style={{ fontSize: "14px", lineHeight: "100%" }}
								>
									Connect to Data Room
								</div>
								<img
									src={connectToDataRoomIcon}
									alt="Connect"
									style={{ width: "16px", height: "16px" }}
								/>
							</button>
						</div>
					</div>
				</div>
			</div>

			<div
				style={{
					position: "absolute",
					right: "2vw",
					left: "50%",
					zIndex: 1,
					maskImage:
						"linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)",
					WebkitMaskImage:
						"linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)",
				}}
			>
				<img
					style={{
						height: "100vh",
					}}
					src={uploadDocsEmptyState}
					alt="Upload documents empty state"
					className="max-w-full h-auto"
				/>
			</div>

			{uploadFlow.element}

		{/* Data Room Connection Popup */}
			<DataRoomConnectionPopup
				isOpen={isDataRoomPopupOpen}
				onClose={() => setIsDataRoomPopupOpen(false)}
				onConnect={handleDataRoomConnect}
			/>
		</div>
	);
};

/**
 * "Let's get started" is the first-run page only: once the user has a deal,
 * the Deal Room entry is the Files home.
 */
const Upload: React.FC = () => {
	const library = useFileLibrary();
	if (library.loading) return <div className="flex-1 bg-white" />;
	if (!library.error && library.deals.length > 0) {
		return <Navigate to="/files" replace />;
	}
	return <GetStarted />;
};

export default Upload;
